import { supabase } from '../lib/supabase';
import { getCurrentUserId } from '../utils/authUtils';

export interface SupportTicket {
  ticket_id: string;
  subject: string;
  description: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  category: string;
  assignee_id?: string;
  created_at: string;
  updated_at: string;
  resolved_at?: string;
  closed_at?: string;
}

export interface CreateTicketRequest {
  subject: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  category: string;
}

export interface UpdateTicketRequest {
  status?: 'open' | 'in_progress' | 'resolved' | 'closed';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  assignee_id?: string;
}

/** Fire-and-forget: send support ticket confirmation email */
async function sendTicketConfirmationEmail(ticket: SupportTicket): Promise<void> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const email = session?.user?.email;
    if (!email) return;
    supabase.functions.invoke('send-funnel-email', {
      body: {
        type: 'support_ticket_created',
        email,
        ticket_id: ticket.ticket_id,
        ticket_subject: ticket.subject,
        ticket_category: ticket.category,
        ticket_priority: ticket.priority,
      },
    }).then(({ data, error }) => {
      if (error) console.warn('[SupportTicket] email trigger failed:', error);
      else console.log('[SupportTicket] confirmation email sent ✅ resend_id:', data?.resend_id);
    }).catch(() => {});
  } catch {
    // Non-blocking — never fail ticket creation because of email
  }
}

/**
 * Get all support tickets for the current user
 */
export async function getSupportTickets(): Promise<SupportTicket[]> {
  try {
    const userId = await getCurrentUserId();
    if (!userId) {
      throw new Error('Authentication required');
    }

    // Check if this is an admin user (non-UUID ID indicates admin)
    const isAdminUser = !userId.includes('-') || userId.startsWith('admin-');
    
    let query = supabase
      .from('support_tickets')
      .select(`
        ticket_id,
        subject,
        description,
        status,
        priority,
        category,
        assignee_id,
        created_at,
        updated_at,
        resolved_at,
        closed_at
      `)
      .order('created_at', { ascending: false});
    
    // Only filter by user_id if not an admin user
    if (!isAdminUser) {
      query = query.eq('user_id', userId);
    }
    
    const { data: tickets, error } = await query;

    if (error) {
      console.error('Error fetching support tickets:', error);
      throw error;
    }

    return tickets || [];
  } catch (error) {
    console.error('Error in getSupportTickets:', error);
    throw error;
  }
}

/**
 * Get a specific support ticket by ID
 */
export async function getSupportTicket(ticketId: string): Promise<SupportTicket> {
  try {
    const userId = await getCurrentUserId();
    if (!userId) {
      throw new Error('Authentication required');
    }

    const { data: ticket, error } = await supabase
      .from('support_tickets')
      .select(`
        ticket_id,
        subject,
        description,
        status,
        priority,
        category,
        assignee_id,
        created_at,
        updated_at,
        resolved_at,
        closed_at
      `)
      .eq('ticket_id', ticketId)
      .eq('user_id', userId)
      .single();

    if (error) {
      console.error('Error fetching support ticket:', error);
      throw error;
    }

    return ticket;
  } catch (error) {
    console.error('Error in getSupportTicket:', error);
    throw error;
  }
}

/**
 * Create a new support ticket
 */
export async function createSupportTicket(ticketData: CreateTicketRequest): Promise<SupportTicket> {
  try {
    const userId = await getCurrentUserId();
    if (!userId) {
      throw new Error('Authentication required');
    }

    const { subject, description, priority, category } = ticketData;

    if (!subject || !description) {
      throw new Error('Subject and description are required');
    }

    const { data: ticket, error } = await supabase
      .from('support_tickets')
      .insert({
        user_id: userId,
        subject: subject.trim(),
        description: description.trim(),
        priority: priority || 'medium',
        category: category || 'General',
        status: 'open'
      })
      .select(`
        ticket_id,
        subject,
        description,
        status,
        priority,
        category,
        assignee_id,
        created_at,
        updated_at,
        resolved_at,
        closed_at
      `)
      .single();

    if (error) {
      console.error('Error creating support ticket:', error);
      throw error;
    }

    // 📧 Send ticket confirmation email (fire-and-forget)
    sendTicketConfirmationEmail(ticket);

    return ticket;
  } catch (error) {
    console.error('Error in createSupportTicket:', error);
    throw error;
  }
}

/**
 * Update a support ticket
 */
export async function updateSupportTicket(
  ticketId: string, 
  updateData: UpdateTicketRequest
): Promise<SupportTicket> {
  try {
    const userId = await getCurrentUserId();
    if (!userId) {
      throw new Error('Authentication required');
    }

    const { data: ticket, error } = await supabase
      .from('support_tickets')
      .update(updateData)
      .eq('ticket_id', ticketId)
      .eq('user_id', userId)
      .select(`
        ticket_id,
        subject,
        description,
        status,
        priority,
        category,
        assignee_id,
        created_at,
        updated_at,
        resolved_at,
        closed_at
      `)
      .single();

    if (error) {
      console.error('Error updating support ticket:', error);
      throw error;
    }

    return ticket;
  } catch (error) {
    console.error('Error in updateSupportTicket:', error);
    throw error;
  }
}

/**
 * Delete a support ticket (soft delete by updating status to closed)
 */
export async function deleteSupportTicket(ticketId: string): Promise<void> {
  try {
    const userId = await getCurrentUserId();
    if (!userId) {
      throw new Error('Authentication required');
    }

    const { error } = await supabase
      .from('support_tickets')
      .update({ status: 'closed' })
      .eq('ticket_id', ticketId)
      .eq('user_id', userId);

    if (error) {
      console.error('Error deleting support ticket:', error);
      throw error;
    }
  } catch (error) {
    console.error('Error in deleteSupportTicket:', error);
    throw error;
  }
}

/**
 * Get ticket statistics for the current user
 */
export async function getTicketStatistics(): Promise<{
  total: number;
  open: number;
  in_progress: number;
  resolved: number;
  closed: number;
}> {
  try {
    const userId = await getCurrentUserId();
    if (!userId) {
      throw new Error('Authentication required');
    }

    // Check if this is an admin user (non-UUID ID indicates admin)
    const isAdminUser = !userId.includes('-') || userId.startsWith('admin-');
    
    let query = supabase
      .from('support_tickets')
      .select('status');
    
    // Only filter by user_id if not an admin user
    if (!isAdminUser) {
      query = query.eq('user_id', userId);
    }
    
    const { data: tickets, error } = await query;

    if (error) {
      console.error('Error fetching ticket statistics:', error);
      throw error;
    }

    const stats = {
      total: tickets?.length || 0,
      open: tickets?.filter(t => t.status === 'open').length || 0,
      in_progress: tickets?.filter(t => t.status === 'in_progress').length || 0,
      resolved: tickets?.filter(t => t.status === 'resolved').length || 0,
      closed: tickets?.filter(t => t.status === 'closed').length || 0,
    };

    return stats;
  } catch (error) {
    console.error('Error in getTicketStatistics:', error);
    throw error;
  }
}
